/**
 * Internet Connectivity Monitor
 * Provides methods to check internet connection status and measure latency
 */

import logger from "../../../utils/logger";

export const CONNECTION_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  SLOW: 'slow',
  CHECKING: 'checking'
};

export const SIGNAL_STRENGTH = {
  EXCELLENT: 'excellent', // < 100ms
  GOOD: 'good',           // 100-300ms
  FAIR: 'fair',           // 300-500ms
  POOR: 'poor',           // 500-1000ms
  VERY_POOR: 'very_poor'  // > 1000ms
};

export const SPEED_LEVEL = {
  VERY_FAST: 'very_fast',   // > 1000 KB/s
  FAST: 'fast',             // 500-1000 KB/s
  MEDIUM: 'medium',         // 200-500 KB/s
  SLOW: 'slow',             // 50-200 KB/s
  VERY_SLOW: 'very_slow'    // < 50 KB/s
};

class InternetPing {
  constructor(options = {}) {
    this.checkInterval = options.checkInterval || 30000; // Check every 30 seconds
    this.timeout = options.timeout || 5000; // 5 second timeout
    this.pingUrls = options.pingUrls || [
      // 'https://trade-live.iuxsecure.com/health/',
      'https://trading.iux.com/favicon.ico',
      'https://www.cloudflare.com/favicon.ico',
      'https://www.google.com/favicon.ico',
      'https://1.1.1.1'
    ];
    // URL for speed test - using a CDN file with CORS enabled (~100KB)
    // Using jsdelivr CDN which has proper CORS headers
    this.speedTestUrl = options.speedTestUrl || 'https://cdn.jsdelivr.net/npm/react@18.2.0/umd/react.production.min.js';
    this.speedTestSize = options.speedTestSize || 100; // KB (approximate)
    this.currentUrl = options.currentUrl || window.location.origin;
    this.listeners = [];
    this.intervalId = null;
    this.isChecking = false;
    this.lastStatus = {
      isOnline: navigator.onLine,
      latency: null,
      strength: null,
      downloadSpeed: null, // KB/s
      speedLevel: null,
      timestamp: Date.now()
    };

    this._setupEventListeners();
  }

  /**
   * Setup browser online/offline event listeners
   */
  _setupEventListeners() {
    window.addEventListener('online', () => this._handleOnlineStatusChange(true));
    window.addEventListener('offline', () => this._handleOnlineStatusChange(false));
  }

  /**
   * Handle browser online/offline events
   */
  _handleOnlineStatusChange(isOnline) {
    this.lastStatus.isOnline = isOnline;
    this.lastStatus.timestamp = Date.now();
    
    if (isOnline) {
      // When coming back online, check the actual connection quality
      this.checkConnection();
    } else {
      this.lastStatus.latency = null;
      this.lastStatus.strength = SIGNAL_STRENGTH.VERY_POOR;
      this.lastStatus.downloadSpeed = null;
      this.lastStatus.speedLevel = SPEED_LEVEL.VERY_SLOW;
      this._notifyListeners();
    }
  }

  /**
   * Ping a URL and measure latency
   */
  async pingUrl(url) {
    const startTime = Date.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const endTime = Date.now();
      const latency = endTime - startTime;

      return { success: true, latency };
    } catch (error) {
      const endTime = Date.now();
      const latency = endTime - startTime;
      
      // If it took the full timeout period, connection is very poor or offline
      if (latency >= this.timeout) {
        return { success: false, latency: null };
      }
      
      // Sometimes fetch with no-cors fails but connection exists
      // If it failed quickly, might still be online
      return { success: false, latency: latency < 1000 ? latency : null };
    }
  }

  /**
   * Test download speed by downloading a file and measuring KB/s
   */
  async checkDownloadSpeed() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout * 2); // Double timeout for download

      const startTime = Date.now();
      
      // Add timestamp to prevent caching
      const url = `${this.speedTestUrl}?t=${Date.now()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
        signal: controller.signal
      });

      if (!response.ok) {
        clearTimeout(timeoutId);
        return { success: false, speed: null };
      }

      // Get the actual size or use the configured size
      const blob = await response.blob();
      const sizeInKB = blob.size / 1024;
      
      clearTimeout(timeoutId);
      const endTime = Date.now();
      const durationInSeconds = (endTime - startTime) / 1000;

      // Calculate speed in KB/s
      const speed = durationInSeconds > 0 ? sizeInKB / durationInSeconds : 0;

      logger.log(`Speed test success: ${Math.round(speed)} KB/s (${sizeInKB.toFixed(2)} KB in ${durationInSeconds.toFixed(2)}s)`);
      return { success: true, speed: Math.round(speed) };
    } catch (error) {
      return { success: false, speed: null };
    }
  }

  /**
   * Determine speed level based on KB/s
   */
  _getSpeedLevel(speed) {
    if (speed === null) return SPEED_LEVEL.VERY_SLOW;
    if (speed > 1000) return SPEED_LEVEL.VERY_FAST;
    if (speed > 500) return SPEED_LEVEL.FAST;
    if (speed > 200) return SPEED_LEVEL.MEDIUM;
    if (speed > 50) return SPEED_LEVEL.SLOW;
    return SPEED_LEVEL.VERY_SLOW;
  }

  /**
   * Check connection using current server or fallback URLs
   */
  async checkConnection() {
    if (this.isChecking) return this.lastStatus;

    this.isChecking = true;
    const results = [];

    // Try pinging multiple URLs for more reliable check
    for (const url of this.pingUrls) {
      const result = await this.pingUrl(url);
      results.push(result);
      
      // If we get a successful ping, we can stop checking
      if (result.success) break;
    }

    // Determine connection status from results
    const successfulPings = results.filter(r => r.success);
    const isOnline = successfulPings.length > 0;
    
    let latency = null;
    let downloadSpeed = null;
    let speedLevel = null;

    if (isOnline) {
      // Use the fastest successful ping
      latency = Math.min(...successfulPings.map(r => r.latency));

      // Check download speed
      const speedResult = await this.checkDownloadSpeed();
      if (speedResult.success) {
        downloadSpeed = speedResult.speed;
        speedLevel = this._getSpeedLevel(downloadSpeed);
      }
    }

    const strength = this._getSignalStrength(latency);

    this.lastStatus = {
      isOnline,
      latency,
      strength,
      downloadSpeed,
      speedLevel,
      timestamp: Date.now()
    };

    this.isChecking = false;
    this._notifyListeners();

    return this.lastStatus;
  }

  /**
   * Determine signal strength based on latency
   */
  _getSignalStrength(latency) {
    if (latency === null) return SIGNAL_STRENGTH.VERY_POOR;
    if (latency < 100) return SIGNAL_STRENGTH.EXCELLENT;
    if (latency < 300) return SIGNAL_STRENGTH.GOOD;
    if (latency < 500) return SIGNAL_STRENGTH.FAIR;
    if (latency < 1000) return SIGNAL_STRENGTH.POOR;
    return SIGNAL_STRENGTH.VERY_POOR;
  }

  /**
   * Get connection status string
   */
  getConnectionStatus() {
    if (this.isChecking) return CONNECTION_STATUS.CHECKING;
    if (!this.lastStatus.isOnline) return CONNECTION_STATUS.OFFLINE;
    
    const { strength } = this.lastStatus;
    if (strength === SIGNAL_STRENGTH.POOR || strength === SIGNAL_STRENGTH.VERY_POOR) {
      return CONNECTION_STATUS.SLOW;
    }
    
    return CONNECTION_STATUS.ONLINE;
  }

  /**
   * Subscribe to connection status changes
   */
  subscribe(callback) {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of status change
   */
  _notifyListeners() {
    const status = {
      ...this.lastStatus,
      connectionStatus: this.getConnectionStatus()
    };
    
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        logger.error('Error in ping listener:', error);
      }
    });
  }

  /**
   * Start automatic periodic checking
   */
  startMonitoring() {
    if (this.intervalId) return; // Already monitoring

    // Do an immediate check
    this.checkConnection();

    // Then check periodically
    this.intervalId = setInterval(() => {
      this.checkConnection();
    }, this.checkInterval);
  }

  /**
   * Stop automatic periodic checking
   */
  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Get current status without checking
   */
  getStatus() {
    return {
      ...this.lastStatus,
      connectionStatus: this.getConnectionStatus()
    };
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.stopMonitoring();
    window.removeEventListener('online', this._handleOnlineStatusChange);
    window.removeEventListener('offline', this._handleOnlineStatusChange);
    this.listeners = [];
  }
}

// Export singleton instance
let pingInstance = null;

export function createPingMonitor(options) {
  if (!pingInstance) {
    pingInstance = new InternetPing(options);
  }
  return pingInstance;
}

export function getPingMonitor() {
  if (!pingInstance) {
    pingInstance = createPingMonitor();
  }
  return pingInstance;
}

export default InternetPing;

